import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import type { Video } from "@/types/video";
import { getHotRanking, searchVideos } from "@/utils/video";
import { usePageMeta } from "@/hooks/usePageMeta";
import VideoCard from "@/components/base/VideoCard";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const [input, setInput] = useState(keyword);

  const list = videos as Video[];
  const results = useMemo(() => (keyword ? searchVideos(list, keyword) : []), [list, keyword]);
  const hot = useMemo(() => getHotRanking(list, 4), [list]);

  usePageMeta(
    keyword
      ? `“${keyword}” 搜索结果 - 91视频在线观看`
      : "搜索影视 - 91视频在线观看高清影视免费在线观看平台",
    keyword
      ? `91视频在线观看为你找到与“${keyword}”相关的影视资源共 ${results.length} 个，支持在线观看与详情查看。`
      : "在91视频在线观看搜索电影、电视剧、综艺，支持标题、类型、地区、演员多维搜索。",
    `91视频在线观看,影视搜索,${keyword || "在线观看"},电影搜索,电视剧搜索`,
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">搜索结果</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {keyword ? `“${keyword}” 的搜索结果` : "全站影视搜索"}
        </h1>
      </header>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3">
          <span className="flex h-10 w-6 items-center justify-center text-foreground-500">
            <i className="ri-search-line"></i>
          </span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影片名称、类型或演员关键词"
            className="h-11 flex-1 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      {keyword && (
        <p className="mt-5 text-sm text-foreground-600">
          共找到 <span className="font-semibold text-primary-500">{results.length}</span> 个结果
        </p>
      )}

      {results.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {results.map((video) => (
            <VideoCard key={video.id} video={video} keyword={keyword} />
          ))}
        </div>
      ) : (
        <div className="mt-10 rounded-lg border border-background-200/70 bg-background-100 p-8">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-3xl"></i>
            </div>
            <p className="mt-5 text-base font-medium text-foreground-900">
              {keyword ? `没有找到与“${keyword}”相关的影视资源` : "输入关键词开始搜索"}
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              换个关键词试试，或看看下面的热门推荐
            </p>
          </div>

          <div className="mt-8">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-fire-fill text-primary-500"></i>
              热门推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {hot.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}